import { ChangeEvent, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon, TrashIcon, PlusIcon, XMarkIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import Notification from "../Shared/Notification";
import KeywordSection from "./KeywordSection";
import PromptPreview from "./PromptPreview";
import RoleSection from "./RoleSection";
import RuleSection from "./RuleSection";
import SectionCard from "./SectionCard";
import SkillsSection from "./SkillsSection";
import TierSection from "./TierSection";
import CustomSectionSection from "./CustomSectionSection";
import useApiToken from "../../hooks/useApiToken";
import { GetRole, GetRolePromptConfig, UpsertRolePromptConfig } from "../../api/roles.api";
import { buildPrompt } from "./promptBuilder";
import { RoleConfig, createEmptyRoleConfig } from "./types";
import {
  PromptConfigPreset,
  GetPromptConfigPresets,
  CreatePromptConfigPreset,
  UpdatePromptConfigPreset,
  DeletePromptConfigPreset,
} from "../../api/promptConfigPresets.api";

type RoleRuleKey = "experience" | "projects" | "extra";
type KeywordGroupKey = "positive" | "negative";

type PromptConfigBuilderProps = {
  initialConfig?: RoleConfig;
  onSave?: (config: RoleConfig, prompt: string, roleId?: string) => void;
  storageKey?: string;
  className?: string;
  showLocalStorageActions?: boolean;
  roleId?: string;
};

type Action =
  | { type: "replace"; payload: RoleConfig }
  | { type: "update"; updater: (prev: RoleConfig) => RoleConfig };

const reducer = (state: RoleConfig, action: Action): RoleConfig => {
  if (action.type === "replace") return action.payload;
  return action.updater(state);
};

const STORAGE_DEFAULT = "prompt-config-builder";

const normalizeConfig = (config: RoleConfig): RoleConfig => ({
  ...config,
  roleName: config.roleName ?? "",
  scoring: {
    skills: config.scoring?.skills ?? [],
    experience: {
      weight: config.scoring?.experience?.weight ?? 0,
      rules: config.scoring?.experience?.rules ?? [],
    },
    projects: {
      weight: config.scoring?.projects?.weight ?? 0,
      rules: config.scoring?.projects?.rules ?? [],
    },
    extra: {
      weight: config.scoring?.extra?.weight ?? 0,
      rules: config.scoring?.extra?.rules ?? [],
    },
  },
  tiers: {
    tier1: {
      min: config.tiers?.tier1?.min ?? 0,
      requirements: config.tiers?.tier1?.requirements ?? [],
    },
    tier2: {
      min: config.tiers?.tier2?.min ?? 0,
      requirements: config.tiers?.tier2?.requirements ?? [],
    },
    tier3: {
      min: config.tiers?.tier3?.min ?? 0,
      requirements: config.tiers?.tier3?.requirements ?? [],
    },
  },
  keywords: {
    positive: config.keywords?.positive ?? {},
    negative: config.keywords?.negative ?? {},
  },
  customSections: config.customSections ?? [],
  outputRequirements: config.outputRequirements ?? [
    "Return a concise reasoning summary.",
    "Return final score as a number.",
    "Return assigned tier (tier1, tier2, or tier3).",
    "Mention strongest positive and risk signals."
  ],
});

export default function PromptConfigBuilder({
  initialConfig,
  onSave,
  storageKey = STORAGE_DEFAULT,
  className = "",
  showLocalStorageActions = true,
  roleId,
}: PromptConfigBuilderProps) {
  const [config, dispatch] = useReducer(reducer, normalizeConfig(initialConfig ?? createEmptyRoleConfig()));
  const [jsonText, setJsonText] = useState("");
  const [notification, setNotification] = useState<{ title: string; description: string; type: "success" | "error" | "info" }>({ title: "", description: "", type: "info" });
  const [showNotification, setShowNotification] = useState(false);

  const showToast = (title: string, description: string, type: "success" | "error" | "info" = "info") => {
    setNotification({ title, description, type });
    setShowNotification(true);
  };

  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingServerConfig, setIsLoadingServerConfig] = useState(false);
  const [roleName, setRoleName] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const token = useApiToken();

  // ── Preset state ────────────────────────────────────────────────────────────
  const [presets, setPresets] = useState<PromptConfigPreset[]>([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [activePreset, setActivePreset] = useState<PromptConfigPreset | null>(null);
  // Tracks the editable name while the user is renaming an active preset
  const [activePresetName, setActivePresetName] = useState("");
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");

  const prompt = useMemo(() => buildPrompt(config, activePreset?.name), [config, activePreset?.name]);
  const roleScopedStorageKey = roleId ? `${storageKey}:${roleId}` : storageKey;

  // ── Fetch presets on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    setIsLoadingPresets(true);
    GetPromptConfigPresets(token)
      .then(setPresets)
      .catch(() => showToast("Failed to load presets", "Could not fetch saved presets from the server.", "error"))
      .finally(() => setIsLoadingPresets(false));
  }, [token]);

  // ── Load role name ──────────────────────────────────────────────────────────
  useEffect(() => {
    const loadRoleName = async () => {
      if (!roleId || !token || roleName) return;
      try {
        const role = await GetRole(roleId, token);
        const nextRoleName = (role?.title ?? "").trim();
        if (nextRoleName) setRoleName(nextRoleName);
      } catch {
        setRoleName("Add Role Name Manually");
      }
    };
    loadRoleName();
  }, [roleId, token, roleName]);

  useEffect(() => {
    if (!roleName) return;
    dispatch({
      type: "update",
      updater: (prev) => {
        if ((prev.roleName ?? "") === roleName) return prev;
        return { ...prev, roleName };
      },
    });
  }, [roleName, config.roleName]);

  // ── Load server prompt config ───────────────────────────────────────────────
  useEffect(() => {
    const loadRolePromptConfig = async () => {
      if (!roleId || !token) return;
      setIsLoadingServerConfig(true);
      try {
        const response = await GetRolePromptConfig(roleId, token);
        if (response.configJson) {
          const parsed = JSON.parse(response.configJson) as RoleConfig;
          dispatch({ type: "replace", payload: normalizeConfig(parsed) });
          showToast("Config loaded", "Loaded saved role prompt configuration.", "success");
        }
      } catch {
        showToast("Load failed", "Failed to load saved role prompt configuration.", "error");
      } finally {
        setIsLoadingServerConfig(false);
      }
    };
    loadRolePromptConfig();
  }, [roleId, token]);

  // ── Generic config updater ──────────────────────────────────────────────────
  const updateConfig = (updater: (prev: RoleConfig) => RoleConfig) => {
    dispatch({ type: "update", updater });
  };

  const setRuleWeight = (key: RoleRuleKey, weight: number) => {
    updateConfig((prev) => ({
      ...prev,
      scoring: { ...prev.scoring, [key]: { ...prev.scoring[key], weight } },
    }));
  };

  const setRuleItem = (key: RoleRuleKey, index: number, value: string) => {
    updateConfig((prev) => {
      const nextRules = [...prev.scoring[key].rules];
      nextRules[index] = value;
      return { ...prev, scoring: { ...prev.scoring, [key]: { ...prev.scoring[key], rules: nextRules } } };
    });
  };

  const addRuleItem = (key: RoleRuleKey) => {
    updateConfig((prev) => ({
      ...prev,
      scoring: { ...prev.scoring, [key]: { ...prev.scoring[key], rules: [...prev.scoring[key].rules, ""] } },
    }));
  };

  const removeRuleItem = (key: RoleRuleKey, index: number) => {
    updateConfig((prev) => ({
      ...prev,
      scoring: { ...prev.scoring, [key]: { ...prev.scoring[key], rules: prev.scoring[key].rules.filter((_, i) => i !== index) } },
    }));
  };

  const setKeywordCategoryName = (group: KeywordGroupKey, category: string, nextName: string) => {
    if (nextName === category) return;
    updateConfig((prev) => {
      const source = prev.keywords[group];
      if (Object.prototype.hasOwnProperty.call(source, nextName)) return prev;
      const nextBuckets: Record<string, string[]> = {};
      Object.entries(source).forEach(([key, values]) => {
        nextBuckets[key === category ? nextName : key] = values;
      });
      return { ...prev, keywords: { ...prev.keywords, [group]: nextBuckets } };
    });
  };

  const setKeywordValue = (group: KeywordGroupKey, category: string, index: number, value: string) => {
    updateConfig((prev) => {
      const nextCategory = [...(prev.keywords[group][category] ?? [])];
      nextCategory[index] = value;
      return { ...prev, keywords: { ...prev.keywords, [group]: { ...prev.keywords[group], [category]: nextCategory } } };
    });
  };

  const addKeyword = (group: KeywordGroupKey, category: string) => {
    updateConfig((prev) => ({
      ...prev,
      keywords: { ...prev.keywords, [group]: { ...prev.keywords[group], [category]: [...(prev.keywords[group][category] ?? []), ""] } },
    }));
  };

  const removeKeyword = (group: KeywordGroupKey, category: string, index: number) => {
    updateConfig((prev) => ({
      ...prev,
      keywords: { ...prev.keywords, [group]: { ...prev.keywords[group], [category]: (prev.keywords[group][category] ?? []).filter((_, i) => i !== index) } },
    }));
  };

  const addCategory = (group: KeywordGroupKey) => {
    updateConfig((prev) => {
      const current = prev.keywords[group];
      let category = "new-category";
      let suffix = 1;
      while (Object.prototype.hasOwnProperty.call(current, category)) {
        category = `new-category-${suffix}`;
        suffix += 1;
      }
      return { ...prev, keywords: { ...prev.keywords, [group]: { ...current, [category]: [""] } } };
    });
  };

  const removeCategory = (group: KeywordGroupKey, category: string) => {
    updateConfig((prev) => {
      const nextBuckets = { ...prev.keywords[group] };
      delete nextBuckets[category];
      return { ...prev, keywords: { ...prev.keywords, [group]: nextBuckets } };
    });
  };

  // ── Preset actions ──────────────────────────────────────────────────────────

  /** Load a preset config into the form without triggering the generic updateConfig (which clears activePreset). */
  const applyPreset = (preset: PromptConfigPreset) => {
    try {
      const parsed = JSON.parse(preset.configJson) as RoleConfig;
      dispatch({ type: "replace", payload: normalizeConfig(parsed) });
      setActivePreset(preset);
      setActivePresetName(preset.name);
      showToast("Preset loaded", `Configuration set to "${preset.name}".`, "success");
    } catch {
      showToast("Preset error", "Failed to parse preset configuration.", "error");
    }
  };

  /** Save current form as a brand-new preset. Fails (with toast) if name already exists. */
  const handleSaveAsPreset = async () => {
    if (!token) return;
    const name = newPresetName.trim();
    if (!name) return;

    setIsSavingPreset(true);
    try {
      const created = await CreatePromptConfigPreset(
        { name, configJson: JSON.stringify(config) },
        token
      );
      setPresets((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setActivePreset(created);
      setActivePresetName(created.name);
      showToast("Preset saved", `"${name}" has been saved as a new preset.`, "success");
      setIsSaveModalOpen(false);
      setNewPresetName("");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save preset.";
      showToast("Save failed", msg, "error");
    } finally {
      setIsSavingPreset(false);
    }
  };

  /** Update the currently active preset with the current form state. */
  const handleUpdatePreset = async () => {
    if (!token || !activePreset) return;
    setIsSavingPreset(true);
    try {
      const updated = await UpdatePromptConfigPreset(
        activePreset.id,
        { name: activePreset.name, configJson: JSON.stringify(config) },
        token
      );
      setPresets((prev) =>
        prev
          .map((p) => (p.id === updated.id ? updated : p))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setActivePreset(updated);
      showToast("Preset updated", `"${updated.name}" has been updated with your new changes.`, "success");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to update preset.";
      showToast("Update failed", msg, "error");
    } finally {
      setIsSavingPreset(false);
    }
  };

  /** Hard-delete a preset after a native confirmation prompt. */
  const handleDeletePreset = async (preset: PromptConfigPreset) => {
    if (!token) return;
    const confirmed = window.confirm(
      `Delete preset "${preset.name}"? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await DeletePromptConfigPreset(preset.id, token);
      setPresets((prev) => prev.filter((p) => p.id !== preset.id));
      if (activePreset?.id === preset.id) {
        setActivePreset(null);
        setActivePresetName("");
      }
      showToast("Preset deleted", `"${preset.name}" has been permanently deleted.`, "info");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to delete preset.";
      showToast("Delete failed", msg, "error");
    }
  };

  // ── Export / Import ─────────────────────────────────────────────────────────

  const exportAsJson = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const safeRoleId = roleId ?? "no-role";
    link.download = `${safeRoleId}-${config.roleName || "prompt-config"}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const readImportFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as RoleConfig;
        dispatch({ type: "replace", payload: normalizeConfig(parsed) });
        setActivePreset(null);
        setActivePresetName("");
        showToast("Imported", "Configuration imported successfully.", "success");
      } catch {
        showToast("Import failed", "The selected file contains invalid JSON.", "error");
      }
    };
    reader.readAsText(file);
  };

  const loadFromText = () => {
    if (!jsonText.trim()) {
      showToast("Nothing to import", "Paste a JSON config before importing.", "info");
      return;
    }
    try {
      const parsed = JSON.parse(jsonText) as RoleConfig;
      dispatch({ type: "replace", payload: normalizeConfig(parsed) });
      setActivePreset(null);
      setActivePresetName("");
      showToast("Imported", "Configuration loaded from JSON text.", "success");
    } catch {
      showToast("Import failed", "The pasted text contains invalid JSON.", "error");
    }
  };

  // ── Local draft ─────────────────────────────────────────────────────────────

  const saveToLocal = () => {
    localStorage.setItem(roleScopedStorageKey, JSON.stringify(config));
    showToast("Draft saved", "Configuration saved locally.", "success");
  };

  const loadFromLocal = () => {
    const raw = localStorage.getItem(roleScopedStorageKey);
    if (!raw) {
      showToast("No draft found", "No saved draft configuration found.", "info");
      return;
    }
    try {
      const parsed = JSON.parse(raw) as RoleConfig;
      dispatch({ type: "replace", payload: normalizeConfig(parsed) });
      setActivePreset(null);
      setActivePresetName("");
      showToast("Draft loaded", "Configuration loaded from saved draft.", "success");
    } catch {
      showToast("Load failed", "Saved draft contains invalid data.", "error");
    }
  };

  const clearLocal = () => {
    localStorage.removeItem(roleScopedStorageKey);
    showToast("Draft cleared", "Saved draft configuration has been removed.", "info");
  };

  // ── Save to server ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    const hasEmptyCategoryName =
      Object.keys(config.keywords.positive).some((category) => !category.trim()) ||
      Object.keys(config.keywords.negative).some((category) => !category.trim());

    if (hasEmptyCategoryName) {
      showToast("Cannot save", "Category name cannot be empty.", "error");
      return;
    }

    if (!roleId || !token) {
      if (onSave) onSave(config, prompt, roleId);
      showToast("Saved locally", "Role or token unavailable — saved in local context only.", "info");
      return;
    }

    setIsSaving(true);
    try {
      await UpsertRolePromptConfig(
        roleId,
        {
          roleId,
          configName: config.roleName ? `${config.roleName} Prompt` : "Default",
          description: `Prompt configuration for role ${config.roleName || roleId}`,
          configJson: JSON.stringify(config),
          generatedPrompt: prompt,
        },
        token
      );
      if (onSave) onSave(config, prompt, roleId);
      showToast("Saved", "Configuration saved successfully.", "success");
    } catch {
      showToast("Save failed", "Failed to save configuration to backend.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={`min-h-screen bg-gray-100 dark:bg-darkbg`}>
    <div className={`mx-auto max-w-[1600px] px-6 py-6 pt-4 ${className}`}>
      {/* Breadcrumbs */}
      <nav className="mb-6 flex items-center gap-2 text-sm">
        {roleId ? (
          <Link
            to={`/roles/${roleId}`}
            className="text-gray-500 dark:text-gray-400 hover:text-entntblue dark:hover:text-entntblue transition-colors"
          >
            {roleName || "Role"}
          </Link>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">{roleName || "Role"}</span>
        )}
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
        {roleId ? (
          <Link
            to={`/roles/${roleId}/edit`}
            className="text-gray-500 dark:text-gray-400 hover:text-entntblue dark:hover:text-entntblue transition-colors"
          >
            Edit
          </Link>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">Edit</span>
        )}
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
        <span className="font-semibold text-gray-900 dark:text-gray-100">Configure Prompt</span>
        {isLoadingServerConfig && (
          <span className="ml-2 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-entntblue border-t-transparent" />
            Loading…
          </span>
        )}
      </nav>

      <Notification
        show={showNotification}
        setShow={setShowNotification}
        title={notification.title}
        description={notification.description}
        type={notification.type}
      />

      {/* Toolbar */}
      <div className="mb-5 flex flex-col gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm dark:border-gray-700 dark:bg-darkbglight relative z-10">
        
        {/* Row 1: Presets & Creation */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Presets
            </span>

            {isLoadingPresets ? (
              <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-entntblue border-t-transparent" />
                Loading presets…
              </span>
            ) : presets.length === 0 ? (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">(No presets)</span>
            ) : (
              presets.map((preset) => (
                <div
                  key={preset.id}
                  className={`group relative flex items-center rounded-lg border transition-all ${
                    activePreset?.id === preset.id
                      ? "border-entntblue bg-entntblue/10 dark:border-entntblue dark:bg-entntblue/20 ring-1 ring-entntblue/50"
                      : "border-gray-200 bg-gray-50 hover:border-entntblue/40 hover:bg-entntblue/5 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-entntblue/50 dark:hover:bg-entntblue/10"
                  }`}
                >
                  {/* Tooltip */}
                  <div className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-gray-700 z-50">
                    Apply {preset.name} preset
                  </div>

                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm font-medium transition-colors rounded-l-lg ${
                       activePreset?.id === preset.id ? "text-entntblue font-semibold" : "text-gray-700 dark:text-gray-300"
                    }`}
                    onClick={() => applyPreset(preset)}
                  >
                    {preset.name}
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-r-lg border-l border-transparent hover:border-red-100 dark:hover:border-red-800"
                    onClick={() => handleDeletePreset(preset)}
                    title={`Delete "${preset.name}" preset`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          <button
            type="button"
            className="flex items-center gap-1.5 rounded-lg border-2 border-entntblue bg-white px-4 py-1.5 text-sm font-semibold text-entntblue hover:bg-entntblue hover:text-white transition-all dark:bg-darkbg dark:hover:bg-entntblue shadow-sm"
            onClick={() => setIsSaveModalOpen(true)}
            title="Create a new reusable preset from the current configuration"
          >
            <PlusIcon className="h-4 w-4" /> Save as New Preset
          </button>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gray-100 dark:bg-gray-700" />

        {/* Row 2: Active Preset Editing & Generic Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {activePreset ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Editing Preset: <span className="font-semibold text-entntblue">{activePreset.name}</span>
                </span>
                <button
                  type="button"
                  className="ml-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-entntblue hover:bg-entntblue/5 hover:text-entntblue transition-all shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                  onClick={handleUpdatePreset}
                  disabled={isSavingPreset}
                  title={`Save your current form changes back to the "${activePreset.name}" preset`}
                >
                  {isSavingPreset ? "Saving…" : "Save Changes to Preset"}
                </button>
              </div>
            ) : (
              <span className="text-sm italic text-gray-400 dark:text-gray-500">
                Unsaved configuration (No preset active)
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Export / Import */}
            <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800">
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                onClick={exportAsJson}
                title="Download configuration as JSON file"
              >
                <ArrowDownTrayIcon className="h-4 w-4" /> Export
              </button>
              <div className="mx-1 h-4 w-px bg-gray-300 dark:bg-gray-600" />
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                title="Upload configuration from JSON file"
              >
                <ArrowUpTrayIcon className="h-4 w-4" /> Import
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json"
                className="hidden"
                onChange={readImportFile}
              />
            </div>

            {/* Local draft actions */}
            {showLocalStorageActions && (
              <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800">
                <button
                  type="button"
                  className="rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                  onClick={saveToLocal}
                  title="Save a draft to your browser's local storage"
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  className="rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                  onClick={loadFromLocal}
                  title="Load the draft from your browser's local storage"
                >
                  Load Draft
                </button>
                <button
                  type="button"
                  className="rounded-md px-3 py-1 text-sm text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                  onClick={clearLocal}
                  title="Clear the draft from your browser's local storage"
                >
                  Clear Draft
                </button>
              </div>
            )}

            {/* Final Save */}
            <button
              type="button"
              className="ml-2 flex items-center gap-1.5 rounded-lg bg-entntblue px-5 py-2 text-sm font-semibold text-white shadow-md hover:bg-entntblue/90 hover:shadow-lg disabled:opacity-60 transition-all"
              onClick={handleSave}
              disabled={isSaving}
              title="Save this configuration to the role and generate prompt"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              {isSaving ? "Saving…" : "Save Role Config"}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="space-y-4">
          {/* <SectionCard title="Role">
            <RoleSection
              roleName={config.roleName}
              onChange={(value) => updateConfig((prev) => ({ ...prev, roleName: value }))}
            />
          </SectionCard> */}

          <SectionCard title="Skills">
            <SkillsSection
              skills={config.scoring.skills}
              onAdd={() =>
                updateConfig((prev) => ({
                  ...prev,
                  scoring: { ...prev.scoring, skills: [...prev.scoring.skills, { name: "", weight: 0 }] },
                }))
              }
              onRemove={(index) =>
                updateConfig((prev) => ({
                  ...prev,
                  scoring: { ...prev.scoring, skills: prev.scoring.skills.filter((_, i) => i !== index) },
                }))
              }
              onChange={(index, key, value) =>
                updateConfig((prev) => {
                  const nextSkills = [...prev.scoring.skills];
                  nextSkills[index] = { ...nextSkills[index], [key]: value };
                  return { ...prev, scoring: { ...prev.scoring, skills: nextSkills } };
                })
              }
            />
          </SectionCard>

          <SectionCard title="Experience">
            <RuleSection
              title="Experience"
              value={config.scoring.experience}
              onWeightChange={(weight) => setRuleWeight("experience", weight)}
              onRuleChange={(index, value) => setRuleItem("experience", index, value)}
              onAddRule={() => addRuleItem("experience")}
              onRemoveRule={(index) => removeRuleItem("experience", index)}
            />
          </SectionCard>

          <SectionCard title="Projects">
            <RuleSection
              title="Projects"
              value={config.scoring.projects}
              onWeightChange={(weight) => setRuleWeight("projects", weight)}
              onRuleChange={(index, value) => setRuleItem("projects", index, value)}
              onAddRule={() => addRuleItem("projects")}
              onRemoveRule={(index) => removeRuleItem("projects", index)}
            />
          </SectionCard>

          <SectionCard title="Extra Signals">
            <RuleSection
              title="Extra Signals"
              value={config.scoring.extra}
              onWeightChange={(weight) => setRuleWeight("extra", weight)}
              onRuleChange={(index, value) => setRuleItem("extra", index, value)}
              onAddRule={() => addRuleItem("extra")}
              onRemoveRule={(index) => removeRuleItem("extra", index)}
            />
          </SectionCard>

          <SectionCard title="Tier Rules">
            <TierSection
              tiers={config.tiers}
              onMinChange={(tier, value) =>
                updateConfig((prev) => ({
                  ...prev,
                  tiers: { ...prev.tiers, [tier]: { ...prev.tiers[tier], min: value } },
                }))
              }
              onRequirementChange={(tier, index, value) =>
                updateConfig((prev) => {
                  const nextRequirements = [...prev.tiers[tier].requirements];
                  nextRequirements[index] = value;
                  return { ...prev, tiers: { ...prev.tiers, [tier]: { ...prev.tiers[tier], requirements: nextRequirements } } };
                })
              }
              onAddRequirement={(tier) =>
                updateConfig((prev) => ({
                  ...prev,
                  tiers: { ...prev.tiers, [tier]: { ...prev.tiers[tier], requirements: [...prev.tiers[tier].requirements, ""] } },
                }))
              }
              onRemoveRequirement={(tier, index) =>
                updateConfig((prev) => ({
                  ...prev,
                  tiers: {
                    ...prev.tiers,
                    [tier]: { ...prev.tiers[tier], requirements: prev.tiers[tier].requirements.filter((_, i) => i !== index) },
                  },
                }))
              }
            />
          </SectionCard>

          <SectionCard title="Keyword Signals">
            <KeywordSection
              positive={config.keywords.positive}
              negative={config.keywords.negative}
              onAddCategory={addCategory}
              onRemoveCategory={removeCategory}
              onRenameCategory={setKeywordCategoryName}
              onAddKeyword={addKeyword}
              onRemoveKeyword={removeKeyword}
              onKeywordChange={setKeywordValue}
            />
          </SectionCard>

          <SectionCard title="Output Requirements">
            <div className="space-y-4">
              <p className="rounded-md border border-blue-200 bg-blue-50 p-2.5 text-sm italic text-blue-700 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-300">
                These rules govern the final JSON structure and summary generated by the AI. Note: A strict JSON structure rule is automatically appended by the server.
              </p>
              <div className="space-y-3">
                {(config.outputRequirements ?? []).map((req, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="mt-2 text-xs font-semibold text-gray-400 dark:text-gray-500 w-5 text-right">{index + 1}.</span>
                    <input
                      type="text"
                      value={req}
                      onChange={(e) =>
                        updateConfig((prev) => {
                          const nextReqs = [...(prev.outputRequirements ?? [])];
                          nextReqs[index] = e.target.value;
                          return { ...prev, outputRequirements: nextReqs };
                        })
                      }
                      className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 dark:border-gray-600 dark:bg-darkbg dark:text-gray-100"
                    />
                    <button
                      type="button"
                      className="mt-1 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30 transition-colors"
                      onClick={() =>
                        updateConfig((prev) => ({
                          ...prev,
                          outputRequirements: (prev.outputRequirements ?? []).filter((_, i) => i !== index),
                        }))
                      }
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-lg border border-entntblue/40 bg-entntblue/5 px-3 py-2 text-sm font-medium text-entntblue hover:bg-entntblue/10 transition-colors"
                onClick={() =>
                  updateConfig((prev) => ({
                    ...prev,
                    outputRequirements: [...(prev.outputRequirements ?? []), ""],
                  }))
                }
              >
                <PlusIcon className="h-4 w-4" /> Add Requirement
              </button>
            </div>
          </SectionCard>

          <SectionCard title="Custom Prompts / Constraints">
            <CustomSectionSection
              sections={config.customSections ?? []}
              onAdd={() =>
                updateConfig((prev) => ({
                  ...prev,
                  customSections: [
                    ...(prev.customSections ?? []),
                    { title: "", content: "", position: "top" },
                  ],
                }))
              }
              onRemove={(index) =>
                updateConfig((prev) => ({
                  ...prev,
                  customSections: (prev.customSections ?? []).filter((_, i) => i !== index),
                }))
              }
              onChange={(index, key, value) =>
                updateConfig((prev) => {
                  const nextSections = [...(prev.customSections ?? [])];
                  nextSections[index] = { ...nextSections[index], [key]: value };
                  return { ...prev, customSections: nextSections };
                })
              }
            />
          </SectionCard>

          <SectionCard title="Import JSON Text">
            <div className="space-y-3">
              <textarea
                className="h-32 w-full rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 font-mono text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-darkbg placeholder-gray-400 focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 transition-colors"
                placeholder="Paste RoleConfig JSON here…"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
              />
              <button
                type="button"
                className="rounded-lg border border-entntblue/40 bg-entntblue/5 px-3 py-2 text-sm font-medium text-entntblue hover:bg-entntblue/10 transition-colors"
                onClick={loadFromText}
              >
                Import From Text
              </button>
            </div>
          </SectionCard>
        </div>

        <div className="xl:sticky xl:top-20 xl:self-start">
          <PromptPreview prompt={prompt} />
        </div>
      </div>
      {/* Save as Preset Modal */}
      {isSaveModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-darkbg border border-gray-100 dark:border-gray-700 transition-all">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Save as New Preset</h3>
              <button
                onClick={() => setIsSaveModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Create a reusable template from your current configuration. This will be available to everyone on the platform.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Preset Name
              </label>
              <input
                type="text"
                autoFocus
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 focus:border-entntblue focus:outline-none focus:ring-2 focus:ring-entntblue/20 dark:border-gray-600 dark:bg-darkbglight dark:text-gray-100"
                placeholder="e.g. Senior Software Engineer"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newPresetName.trim()) {
                    handleSaveAsPreset();
                  } else if (e.key === 'Escape') {
                    setIsSaveModalOpen(false);
                  }
                }}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsSaveModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAsPreset}
                disabled={isSavingPreset || !newPresetName.trim()}
                className="rounded-lg bg-entntblue px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntblue/90 disabled:opacity-50 transition-colors"
              >
                {isSavingPreset ? "Saving..." : "Save Preset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
